import { supabase } from '../helper/supabaseClient';

function cleanPost(row) {
  return {
    id: row.id,
    title: row.title,
    body: row.body ?? null,
    imageUrl: row.image_url ?? null,
    postType: row.post_type ?? null,
    category: row.category ?? null,
    isBrandRelated: Boolean(row.is_brand_related),
    brandName: row.brand_name ?? null,
    linkedOfferId: row.linked_offer_id ?? null,
    status: row.status,
    publishAt: row.publish_at ?? null,
    createdBy: row.created_by ?? null,
    approvedBy: row.approved_by ?? null,
    sourceActionId: row.source_action_id ?? null,
    ctaText: row.cta_text ?? null,
    ctaLink: row.cta_link ?? null,
    priorityOrder: row.priority_order ?? 0,
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
  };
}

function toIsoOrNull(datetimeLocalValue) {
  if (!datetimeLocalValue) return null;
  const d = new Date(datetimeLocalValue);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function prefillPostFromAction(action) {
  if (!action) return {};
  const title = action.title ? `Action spotlight: ${action.title}` : 'Action spotlight';
  const body = action.description || null;
  const imageUrl = action.photo_url || action.photo || null;
  return {
    title,
    body,
    image_url: imageUrl,
    post_type: 'action_spotlight',
    category: action.category || null,
    source_action_id: action.id || null,
  };
}

export async function getPublishedCommunityPosts() {
  const nowIso = new Date().toISOString();

  const { data, error } = await supabase
    .from('community_posts')
    .select(
      [
        'id',
        'title',
        'body',
        'image_url',
        'post_type',
        'category',
        'is_brand_related',
        'brand_name',
        'linked_offer_id',
        'status',
        'publish_at',
        'created_by',
        'approved_by',
        'source_action_id',
        'cta_text',
        'cta_link',
        'priority_order',
        'created_at',
        'updated_at',
      ].join(',')
    )
    .eq('status', 'published')
    .or(`publish_at.is.null,publish_at.lte.${nowIso}`)
    .order('priority_order', { ascending: false })
    .order('publish_at', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message || 'Failed to load posts');
  return (data ?? []).map(cleanPost);
}

export async function getManageableCommunityPosts() {
  // RLS decides what comes back:
  // - admin: all posts
  // - editor: their own posts
  const { data, error } = await supabase
    .from('community_posts')
    .select(
      [
        'id',
        'title',
        'body',
        'image_url',
        'post_type',
        'category',
        'is_brand_related',
        'brand_name',
        'linked_offer_id',
        'status',
        'publish_at',
        'created_by',
        'approved_by',
        'source_action_id',
        'cta_text',
        'cta_link',
        'priority_order',
        'created_at',
        'updated_at',
      ].join(',')
    )
    .order('updated_at', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message || 'Failed to load posts');
  return (data ?? []).map(cleanPost);
}

export async function createCommunityPost(input, currentUserId) {
  const payload = {
    title: input.title,
    body: input.body || null,
    image_url: input.image_url || null,
    post_type: input.post_type || null,
    category: input.category || null,
    is_brand_related: Boolean(input.is_brand_related),
    brand_name: input.brand_name || null,
    linked_offer_id: input.linked_offer_id || null,
    status: input.status || 'draft',
    publish_at: toIsoOrNull(input.publish_at) ?? input.publish_at ?? null,
    created_by: currentUserId ?? null,
    cta_text: input.cta_text || null,
    cta_link: input.cta_link || null,
    priority_order: Number.isFinite(Number(input.priority_order)) ? Number(input.priority_order) : 0,
    source_action_id: input.source_action_id || null,
  };

  const { data, error } = await supabase
    .from('community_posts')
    .insert([payload])
    .select()
    .single();

  if (error) throw new Error(error.message || 'Failed to create post');
  return cleanPost(data);
}

export async function updateCommunityPost(id, input) {
  const payload = {
    title: input.title,
    body: input.body || null,
    image_url: input.image_url || null,
    post_type: input.post_type || null,
    category: input.category || null,
    is_brand_related: Boolean(input.is_brand_related),
    brand_name: input.brand_name || null,
    linked_offer_id: input.linked_offer_id || null,
    status: input.status,
    publish_at: toIsoOrNull(input.publish_at) ?? input.publish_at ?? null,
    cta_text: input.cta_text || null,
    cta_link: input.cta_link || null,
    priority_order: Number.isFinite(Number(input.priority_order)) ? Number(input.priority_order) : 0,
  };

  const { data, error } = await supabase
    .from('community_posts')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(error.message || 'Failed to update post');
  return cleanPost(data);
}

export async function publishCommunityPost(id, adminUserId) {
  const payload = {
    status: 'published',
    approved_by: adminUserId ?? null,
    publish_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from('community_posts')
    .update(payload)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(error.message || 'Failed to publish post');
  return cleanPost(data);
}

